import { createRouter, createWebHistory } from 'vue-router'
import { defineAsyncComponent, type Component } from 'vue'
import useAuth from '../composables/Auth'
import RouteLoading from '../components/RouteLoading.vue'

const { authenticated, user } = useAuth()
const isTestEnv = import.meta.env.MODE != 'production' && import.meta.env.MODE != 'staging'

const hasInsightsAccess = () => {
  const username = (user.value as { username?: string })?.username
  return ['M0017668', 'U008310'].includes(username ?? '')

  // Previous access-level check:
  // const userAccess = (user.value as { access?: number })?.access
  // return userAccess !== undefined && userAccess !== null && [5, 6, 7, 9, 10].includes(userAccess)
}

const hasAdminAccess = () => {
  const userAccess = (user.value as { access?: number })?.access
  return userAccess !== undefined && userAccess !== null && [6, 7, 9, 10].includes(userAccess)
}

const createAsyncRoute = (component: () => Promise<Component>) => {
  return defineAsyncComponent({
    loader: component,
    loadingComponent: RouteLoading,
    delay: 200,
    timeout: 10000,
    errorComponent: defineAsyncComponent(() =>
      import('../components/RouteError.vue').catch(() => RouteLoading),
    ),
  })
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'Home',
      component: createAsyncRoute(() => import('../pages/Home.vue')),
      beforeEnter: () => {
        if (isTestEnv) return
        if (authenticated.value) return { path: '/reporting' }
      },
    },
    {
      path: '/reporting',
      name: 'Statements',
      component: createAsyncRoute(() => import('../pages/Statements.vue')),
      beforeEnter: () => {
        if (isTestEnv) return
        if (!authenticated.value) return { path: '/' }
      },
    },
    {
      path: '/action-plans',
      name: 'ActionPlans',
      component: createAsyncRoute(() => import('../pages/ActionPlans.vue')),
      beforeEnter: () => {
        if (isTestEnv) return
        if (!authenticated.value) return { path: '/' }
      },
    },
    {
      path: '/insights',
      name: 'Insights',
      component: createAsyncRoute(() => import('../pages/Insights.vue')),
      beforeEnter: () => {
        if (isTestEnv) return
        if (!authenticated.value) return { path: '/' }
        if (!hasInsightsAccess()) {
          return { path: '/reporting' }
        }
      },
    },
    {
      path: '/admin',
      name: 'Admin',
      component: createAsyncRoute(() => import('../pages/Admin.vue')),
      beforeEnter: () => {
        if (isTestEnv) return
        if (!authenticated.value) return { path: '/' }
        if (!hasAdminAccess()) {
          return { path: '/reporting' }
        }
      },
    },
    {
      path: '/cookie-policy',
      name: 'CookiePolicy',
      component: createAsyncRoute(() => import('../pages/CookiePolicy.vue')),
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: createAsyncRoute(() => import('../pages/PrivacyPolicy.vue')),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: createAsyncRoute(() => import('../pages/NotFound.vue')),
      beforeEnter: () => {
        if (isTestEnv) return
        if (!authenticated.value) return { path: '/' }
      },
    },
  ],
})

export default router
